import { loadScript } from '@mews-framework/utils';
import { LoggerService } from '../logging';
import { ContextWithAptrinsic, GainsightService } from './types';

interface Params {
    loggerService: LoggerService;
    iframeModeEnabled?: boolean;
    kcAllowedFuncNames?: string[];
}

export const createGainsightService = (
    context: Window,
    apiToken: string | undefined,
    { loggerService, iframeModeEnabled, kcAllowedFuncNames }: Params
): GainsightService => {
    const contextWithAptrinsic = context as ContextWithAptrinsic;

    const initAptrinsic = (...args: Array<unknown>) => {
        contextWithAptrinsic.aptrinsic.q = contextWithAptrinsic.aptrinsic.q || [];
        contextWithAptrinsic.aptrinsic.q.push(args);
    };

    contextWithAptrinsic.aptrinsic = contextWithAptrinsic.aptrinsic || initAptrinsic;

    contextWithAptrinsic.aptrinsic.p = apiToken;

    contextWithAptrinsic.aptrinsic.c = {
        ...(iframeModeEnabled !== undefined ? { iframeModeEnabled } : {}),
        ...(kcAllowedFuncNames !== undefined ? { kcAllowedFuncNames } : {}),
    };

    const initialize = async () => {
        if (apiToken === undefined) return;
        try {
            const gainsightUrl = `https://web-sdk-eu.aptrinsic.com/api/aptrinsic.js?a=${apiToken}`;
            await loadScript(gainsightUrl, {}, context);

            if (!contextWithAptrinsic.aptrinsic?.init) throw new Error();
        } catch (error) {
            loggerService.log('script-load-error', 'Gainsight failed to initialize.', {
                error,
            });
        }
    };

    const setUser: GainsightService['setUser'] = (properties) => {
        const {
            id,
            enterpriseId,
            name,
            chainId,
            chainName,
            email,
            firstName,
            lastName,
            signUpDate,
            segment,
            plan,
            supportTier,
            has2fa,
        } = properties;

        if (!id) {
            return;
        }

        contextWithAptrinsic.aptrinsic(
            'identify',
            {
                id,
                email,
                firstName,
                lastName,
                signUpDate,
                plan,
                has2fa,
            },
            {
                id: enterpriseId,
                chainId,
                chainName,
                name,
                supportTier,
                segment,
            }
        );
    };

    const trackPageView: GainsightService['trackPageView'] = (pageDetails) => {
        contextWithAptrinsic.aptrinsic('pageView', pageDetails);
    };

    const trackEvent: GainsightService['trackEvent'] = (eventName, eventData) => {
        contextWithAptrinsic.aptrinsic('track', eventName, eventData);
    };

    const reset: GainsightService['reset'] = () => {
        contextWithAptrinsic.aptrinsic('reset');
    };

    const setGlobalContext: GainsightService['setGlobalContext'] = (contextData) => {
        contextWithAptrinsic.aptrinsic('set', 'globalContext', contextData);
    };

    const closeKnowledgeCenter: GainsightService['closeKnowledgeCenter'] = () => {
        contextWithAptrinsic.aptrinsic('kcb', 'close');
    };

    return {
        initialize,
        setUser,
        trackPageView,
        trackEvent,
        setGlobalContext,
        reset,
        closeKnowledgeCenter,
    };
};
